import * as THREE from 'three';
import * as CANNON from 'cannon-es';

export class Car {
  constructor(scene, physicsWorld, team, startPosition) {
    this.scene = scene;
    this.physicsWorld = physicsWorld;
    this.team = team;
    this.startPosition = startPosition;
    
    // Debug flag
    this.debug = true;
    
    // Car properties - Rocket League style physics
    this.maxSpeed = 55; // Revert to original top speed
    this.acceleration = 400; // Revert to original acceleration
    this.deceleration = 4; // Revert to original deceleration
    this.turnSpeed = 3; // Revert to original turn speed
    this.jumpForce = 10; // Revert to original jump force
    this.jumpCooldown = 0;
    this.canDoubleJump = false;
    this.hasDoubleJumped = false;
    this.fallMultiplier = 6.0;
    this.boostForce = 500; // Revert to original boost force
    this.boostAmount = 100;
    this.isOnGround = true;
    this.airControl = 0.3; // Revert to original air control
    this.isFrozen = false;
    
    // Controls state
    this.controls = {
      forward: 0,
      backward: 0,
      left: 0,
      right: 0,
      jump: false,
      boost: false
    };
    
    // Previous jump state for tracking button release and press
    this.prevJumpState = false;
    
    if (this.debug) {
      console.log(`Creating car (team: ${team}) at position:`, startPosition);
    }
    
    // Create car mesh and physics body
    this.createCarMesh();
    this.createCarBody();
    
    // Add to scene and physics world
    this.scene.add(this.mesh);
  }
  
  createCarMesh() {
    // Create a more advanced car model similar to the Rocket League style car
    
    // Main car body - we'll create a more complex shape
    const carMaterial = new THREE.MeshPhongMaterial({ 
      color: this.team === 'blue' ? 0x0066ff : 0xffaa00, // Brighter yellow for orange team
      shininess: 100,
      specular: 0x111111
    });
    
    // Create a group to hold all car parts
    this.mesh = new THREE.Group();
    
    // Main body - slightly more complex shape
    const bodyGeometry = new THREE.BoxGeometry(2.2, 0.8, 3.2);
    const carBody = new THREE.Mesh(bodyGeometry, carMaterial);
    carBody.position.y = 0.1;
    this.mesh.add(carBody);
    
    // Roof - sloped shape for sports car look
    const roofGeometry = new THREE.BoxGeometry(1.8, 0.4, 2);
    const roof = new THREE.Mesh(roofGeometry, carMaterial);
    roof.position.set(0, 0.65, -0.2);
    this.mesh.add(roof);
    
    // Front hood/bumper - lower and more angled
    const hoodGeometry = new THREE.BoxGeometry(2, 0.4, 0.8);
    const hood = new THREE.Mesh(hoodGeometry, carMaterial);
    hood.position.set(0, 0.1, 1.8);
    this.mesh.add(hood);
    
    // Rear spoiler
    const spoilerGeometry = new THREE.BoxGeometry(1.8, 0.3, 0.3);
    const spoiler = new THREE.Mesh(spoilerGeometry, carMaterial);
    spoiler.position.set(0, 0.8, -1.7);
    this.mesh.add(spoiler);
    
    // Spoiler supports
    const supportGeometry = new THREE.BoxGeometry(0.1, 0.3, 0.4);
    const leftSupport = new THREE.Mesh(supportGeometry, carMaterial);
    leftSupport.position.set(-0.8, 0.65, -1.6);
    this.mesh.add(leftSupport);
    
    const rightSupport = new THREE.Mesh(supportGeometry, carMaterial);
    rightSupport.position.set(0.8, 0.65, -1.6);
    this.mesh.add(rightSupport);
    
    // Front and rear bumpers (darker color)
    const bumperMaterial = new THREE.MeshPhongMaterial({ 
      color: this.team === 'blue' ? 0x004080 : 0xcc7700,
      shininess: 60
    });
    
    const frontBumperGeometry = new THREE.BoxGeometry(2.2, 0.4, 0.4);
    const frontBumper = new THREE.Mesh(frontBumperGeometry, bumperMaterial);
    frontBumper.position.set(0, 0, 2.0);
    this.mesh.add(frontBumper);
    
    const rearBumperGeometry = new THREE.BoxGeometry(2.2, 0.4, 0.4);
    const rearBumper = new THREE.Mesh(rearBumperGeometry, bumperMaterial);
    rearBumper.position.set(0, 0, -1.8);
    this.mesh.add(rearBumper);
    
    // Headlights
    const headlightMaterial = new THREE.MeshPhongMaterial({
      color: 0xffffff,
      emissive: 0xffffcc,
      emissiveIntensity: 0.5,
      shininess: 100
    });
    
    const headlightGeometry = new THREE.BoxGeometry(0.4, 0.2, 0.1);
    
    const leftHeadlight = new THREE.Mesh(headlightGeometry, headlightMaterial);
    leftHeadlight.position.set(-0.7, 0.3, 2.05);
    this.mesh.add(leftHeadlight);
    
    const rightHeadlight = new THREE.Mesh(headlightGeometry, headlightMaterial);
    rightHeadlight.position.set(0.7, 0.3, 2.05);
    this.mesh.add(rightHeadlight);
    
    // Wheels - slightly larger and with better details
    const wheelGeometry = new THREE.CylinderGeometry(0.5, 0.5, 0.4, 16);
    const wheelMaterial = new THREE.MeshPhongMaterial({ color: 0x111111 });
    
    // Position the wheels
    const wheelPositions = [
      { x: -1.1, y: -0.25, z: 1.2 },  // Front left
      { x: 1.1, y: -0.25, z: 1.2 },   // Front right
      { x: -1.1, y: -0.25, z: -1.2 }, // Rear left
      { x: 1.1, y: -0.25, z: -1.2 }   // Rear right
    ];
    
    this.wheels = wheelPositions.map(pos => {
      const wheel = new THREE.Mesh(wheelGeometry, wheelMaterial);
      wheel.position.set(pos.x, pos.y, pos.z);
      wheel.rotation.z = Math.PI / 2;
      this.mesh.add(wheel);
      return wheel;
    });
    
    // Add the rim details
    const rimMaterial = new THREE.MeshPhongMaterial({ color: 0x888888 });
    this.wheels.forEach(wheel => {
      const rimGeometry = new THREE.CylinderGeometry(0.25, 0.25, 0.41, 8);
      const rim = new THREE.Mesh(rimGeometry, rimMaterial);
      rim.rotation.copy(wheel.rotation);
      wheel.add(rim);
    });
    
    // Position car mesh
    this.mesh.position.set(
      this.startPosition.x,
      this.startPosition.y,
      this.startPosition.z
    );
    
    // Make sure the car casts and receives shadows
    this.mesh.traverse(child => {
      if (child.isMesh) {
        child.castShadow = true;
        child.receiveShadow = true;
      }
    });
    
    if (this.debug) {
      console.log(`Car mesh created with color: ${this.team === 'blue' ? 'blue' : 'orange'}`);
    }
  }
  
  createCarBody() {
    // Create main body shape
    const carShape = new CANNON.Box(new CANNON.Vec3(1, 0.5, 1.5));
    
    // Improved physics body for more responsive movement
    this.body = new CANNON.Body({
      mass: 100, // Revert to original mass
      position: new CANNON.Vec3(
        this.startPosition.x,
        this.startPosition.y,
        this.startPosition.z
      ),
      material: this.physicsWorld.carMaterial,
      linearDamping: 0.1, // Revert to original damping
      angularDamping: 0.5, // Revert to original angular damping
      fixedRotation: false,
      allowSleep: false
    });
    
    this.body.addShape(carShape);
    this.physicsWorld.addBody(this.body);
    
    // Set inertia tensor values for more stable rotation
    this.body.updateMassProperties();
    
    // Add sleep thresholds
    this.body.sleepSpeedLimit = 0.2;
    this.body.sleepTimeLimit = 1;
    
    if (this.debug) {
      console.log('Car physics body created and added to physics world');
    }
  }
  
  update(delta) {
    // CRITICAL: Always ensure dynamic body type and non-frozen state
    this.body.type = CANNON.Body.DYNAMIC;
    this.isFrozen = false;
    this.body.wakeUp();
    
    // Update mesh position to match physics body
    this.mesh.position.copy(this.body.position);
    this.mesh.quaternion.copy(this.body.quaternion);
    
    // Check if we're on ground for jump mechanics
    this.checkGroundContact();
    
    // Apply air drag when in the air to simulate air resistance
    if (!this.isOnGround) {
      // Apply gravity multiplier for better jump arc
      const gravity = new CANNON.Vec3(0, -9.8 * this.fallMultiplier, 0);
      this.body.applyForce(
        gravity.scale(this.body.mass),
        new CANNON.Vec3(0, 0, 0)
      );
      
      // Gentle air drag to prevent excessive speed
      const drag = 0.01;
      this.body.velocity.scale(1 - drag * delta);
    }
    
    // Keep the car upright when on the ground
    if (this.isOnGround) {
      this.stabilizeRotation();
    }
    
    // Update wheel rotation based on current speed
    const speed = Math.sqrt(
      this.body.velocity.x * this.body.velocity.x + 
      this.body.velocity.z * this.body.velocity.z
    );
    const wheelRotationSpeed = speed * 0.3;
    
    // Rotate wheels based on car's current movement
    this.wheels.forEach(wheel => {
      // Determine direction based on forward/backward input
      const direction = this.controls.backward > 0 ? -1 : 1;
      
      // Only rotate wheels when car is moving
      if (speed > 0.5) {
        wheel.rotation.x += wheelRotationSpeed * direction * delta;
      }
    });
    
    // Update boost particles if boosting
    if (this.controls.boost && this.boostAmount > 0) {
      this.updateBoostParticles(delta);
    }
    
    // Update boost bar in UI
    this.updateBoostBar();
    
    // Check for ball collision
    this.checkBallCollision();
    
    // Decrease jump cooldown
    if (this.jumpCooldown > 0) {
      this.jumpCooldown -= 1;
    }
  }
  
  // New method to keep car upright
  stabilizeRotation() {
    // Get current rotation
    const euler = new THREE.Euler().setFromQuaternion(this.body.quaternion);
    
    // Only stabilize x and z rotation (keep car upright)
    if (Math.abs(euler.x) > 0.1 || Math.abs(euler.z) > 0.1) {
      // More aggressive stabilization
      euler.x *= 0.85;
      euler.z *= 0.85;
      
      // Convert back to quaternion
      const targetQuaternion = new THREE.Quaternion().setFromEuler(euler);
      
      // Stronger correction
      this.body.quaternion.slerp(targetQuaternion, 0.2);
      
      // Add downward force when tilted to help stabilize
      if (Math.abs(euler.x) > 0.3 || Math.abs(euler.z) > 0.3) {
        this.body.applyForce(
          new CANNON.Vec3(0, -200, 0),
          new CANNON.Vec3()
        );
      }
    }
  }
  
  checkGroundContact() {
    const carQuat = this.body.quaternion;
    const rayLength = 1.2; // Reduced ray length
    const rayStartOffset = 0.6; // Adjusted offset
    
    // Create ray start positions for each wheel
    const rayStarts = [
      { x: -rayStartOffset, y: 0.3, z: rayStartOffset }, // front left
      { x: rayStartOffset, y: 0.3, z: rayStartOffset },  // front right
      { x: -rayStartOffset, y: 0.3, z: -rayStartOffset }, // rear left
      { x: rayStartOffset, y: 0.3, z: -rayStartOffset }   // rear right
    ];
    
    // Convert local positions to world positions
    const worldRayStarts = rayStarts.map(localPos => {
      const rotatedPos = new THREE.Vector3(localPos.x, localPos.y, localPos.z)
        .applyQuaternion(new THREE.Quaternion(carQuat.x, carQuat.y, carQuat.z, carQuat.w));
      
      return new CANNON.Vec3(
        this.body.position.x + rotatedPos.x,
        this.body.position.y + rotatedPos.y,
        this.body.position.z + rotatedPos.z
      );
    });
    
    const rayEndOffset = new CANNON.Vec3(0, -rayLength, 0);
    let hitCount = 0;
    let closestDistance = rayLength;
    
    worldRayStarts.forEach((rayStart, index) => {
      const rayEnd = new CANNON.Vec3().copy(rayStart).vadd(rayEndOffset);
      const ray = new CANNON.Ray(rayStart, rayEnd);
      ray.mode = CANNON.Ray.CLOSEST;
      ray.skipBackfaces = true;
      
      const result = new CANNON.RaycastResult();
      ray.intersectWorld(this.physicsWorld.world, { result });
      
      if (result.hasHit && result.distance < rayLength * 0.8) {
        hitCount++;
        
        // More gentle ground adjustment
        if (result.distance < 0.2) {
          this.body.position.y += (0.2 - result.distance) * 0.5;
          this.body.velocity.y = Math.max(0, this.body.velocity.y);
        }
        
        if (result.distance < closestDistance) {
          closestDistance = result.distance;
          this.groundContactNormal = result.hitNormalWorld;
        }
      }
    });
    
    const wasOnGround = this.isOnGround;
    this.isOnGround = hitCount >= 2;
    
    if (this.isOnGround && !wasOnGround) {
      this.hasDoubleJumped = false;
      this.canDoubleJump = false;
      this.jumpCooldown = 5; // Reduced cooldown
      
      // Gentler landing bounce
      if (this.body.velocity.y < -8) {
        this.body.velocity.y = Math.abs(this.body.velocity.y) * 0.1;
      }
    }
    
    // Reset position if car falls through ground
    if (this.body.position.y < -5) {
      this.reset();
    }
  }
  
  applyPhysicsForces() {
    // Get the forward direction vector of the car
    const forwardDir = new THREE.Vector3(0, 0, 1);
    forwardDir.applyQuaternion(this.mesh.quaternion);
    forwardDir.y = 0; // Keep movement on ground plane
    forwardDir.normalize();
    
    // Get right vector for lateral force calculations
    const rightDir = new THREE.Vector3(1, 0, 0);
    rightDir.applyQuaternion(this.mesh.quaternion);
    rightDir.y = 0;
    rightDir.normalize();
    
    // Determine if we're in the air
    const isInAir = !this.isOnGround;
    
    // Check if boosting and has boost available
    const isBoosting = this.controls.boost && this.boostAmount > 0;
    
    // Deduct boost amount when using it
    if (isBoosting) {
      this.boostAmount = Math.max(0, this.boostAmount - 0.5); // Drain boost at appropriate rate
    }
    
    // Smooth out inputs to reduce jitter
    // This adds a bit of input smoothing which makes movement less twitchy
    const smoothForward = this.smoothInput(this.controls.forward, '_lastForward', 0.2);
    const smoothBackward = this.smoothInput(this.controls.backward, '_lastBackward', 0.2);
    const smoothLeft = this.smoothInput(this.controls.left, '_lastLeft', 0.15);
    const smoothRight = this.smoothInput(this.controls.right, '_lastRight', 0.15);
    
    // Apply appropriate forces based on controls and state
    if (this.isOnGround) {
      // Calculate lateral velocity for drift control
      const currentVel = new THREE.Vector3(this.body.velocity.x, 0, this.body.velocity.z);
      const lateralVel = rightDir.clone().multiplyScalar(rightDir.dot(currentVel));
      
      // Ground movement
      if (smoothForward > 0) {
        // Forward acceleration - apply force in car's forward direction
        const forceMultiplier = isBoosting ? this.boostForce : this.acceleration;
        this.body.applyForce(
          new CANNON.Vec3(
            forwardDir.x * forceMultiplier * smoothForward,
            0,
            forwardDir.z * forceMultiplier * smoothForward
          ),
          new CANNON.Vec3()
        );
      }
      else if (smoothBackward > 0) {
        // Backward acceleration
        this.body.applyForce(
          new CANNON.Vec3(
            -forwardDir.x * this.acceleration * 0.6 * smoothBackward, // Reduced backward force
            0,
            -forwardDir.z * this.acceleration * 0.6 * smoothBackward
          ),
          new CANNON.Vec3()
        );
      }
      
      // Apply counter force to lateral movement for better drifting control
      const lateralDampingFactor = 4.0; // Higher values mean less drifting
      this.body.applyForce(
        new CANNON.Vec3(
          -lateralVel.x * lateralDampingFactor,
          0,
          -lateralVel.z * lateralDampingFactor
        ),
        new CANNON.Vec3()
      );
      
      // Apply turning torque when on ground - smoothed to prevent jerky turning
      if (smoothLeft > 0) {
        const turnForce = this.turnSpeed * smoothLeft * (1 - 0.3 * Math.min(1, currentVel.length() / this.maxSpeed));
        this.body.applyTorque(new CANNON.Vec3(0, turnForce, 0));
      }
      else if (smoothRight > 0) {
        const turnForce = -this.turnSpeed * smoothRight * (1 - 0.3 * Math.min(1, currentVel.length() / this.maxSpeed));
        this.body.applyTorque(new CANNON.Vec3(0, turnForce, 0));
      }
      
      // Ground friction when no input is given - progressive deceleration
      if (smoothForward === 0 && smoothBackward === 0 && !isBoosting) {
        const speed = currentVel.length();
        const frictionFactor = this.deceleration * (1 + 0.05 * speed); // Progressive friction based on speed
        
        // Apply friction to slow down
        if (speed > 0.5) { // Only apply friction above a minimum speed
          const frictionForce = new CANNON.Vec3(
            -this.body.velocity.x * frictionFactor,
            0,
            -this.body.velocity.z * frictionFactor
          );
          this.body.applyForce(frictionForce, new CANNON.Vec3());
        }
      }
    } 
    else {
      // Air movement - reduced control but still responsive
      if (smoothForward > 0) {
        // Air forward control - reduced force
        const airForceMultiplier = isBoosting ? this.boostForce : (this.acceleration * this.airControl);
        
        // Apply forward force in air
        this.body.applyForce(
          new CANNON.Vec3(
            forwardDir.x * airForceMultiplier * smoothForward,
            0,
            forwardDir.z * airForceMultiplier * smoothForward
          ),
          new CANNON.Vec3()
        );
      }
      
      // Air turning - reduced torque
      if (smoothLeft > 0) {
        this.body.applyTorque(new CANNON.Vec3(0, this.turnSpeed * this.airControl * smoothLeft, 0));
      }
      else if (smoothRight > 0) {
        this.body.applyTorque(new CANNON.Vec3(0, -this.turnSpeed * this.airControl * smoothRight, 0));
      }
      
      // Air roll effect - smoother and more controlled
      if (smoothLeft > 0) {
        this.body.applyTorque(new CANNON.Vec3(0, 0, this.turnSpeed * 0.5 * smoothLeft));
      }
      else if (smoothRight > 0) {
        this.body.applyTorque(new CANNON.Vec3(0, 0, -this.turnSpeed * 0.5 * smoothRight));
      }
      
      // Boosting in air
      if (isBoosting) {
        // Apply boost force in the direction the car is facing
        const boostDir = new THREE.Vector3(0, 0, 1);
        boostDir.applyQuaternion(this.mesh.quaternion);
        
        this.body.applyForce(
          new CANNON.Vec3(
            boostDir.x * this.boostForce * 0.7, // Reduced boost force in air
            boostDir.y * this.boostForce * 0.7,
            boostDir.z * this.boostForce * 0.7
          ),
          new CANNON.Vec3()
        );
      }
      
      // Add slight air resistance for better aerial control and prevent infinite skating
      const airResistanceDir = new THREE.Vector3(
        -this.body.velocity.x,
        -this.body.velocity.y,
        -this.body.velocity.z
      ).normalize();
      
      const airSpeed = Math.sqrt(
        this.body.velocity.x * this.body.velocity.x +
        this.body.velocity.y * this.body.velocity.y +
        this.body.velocity.z * this.body.velocity.z
      );
      
      // Apply air resistance - quadratic with speed (higher at higher speeds)
      const airResistance = 0.02 * airSpeed * airSpeed;
      this.body.applyForce(
        new CANNON.Vec3(
          airResistanceDir.x * airResistance,
          airResistanceDir.y * airResistance,
          airResistanceDir.z * airResistance
        ),
        new CANNON.Vec3()
      );
    }
    
    // Apply jump force
    if (this.controls.jump) {
      if (this.isOnGround && this.jumpCooldown === 0) {
        // First jump
        this.body.applyImpulse(new CANNON.Vec3(0, this.jumpForce, 0), new CANNON.Vec3());
        this.jumpCooldown = 10;
        this.canDoubleJump = true;
      }
      // Double jump if in air, has double jump available, and hasn't used it yet
      else if (!this.isOnGround && this.canDoubleJump && !this.hasDoubleJumped) {
        this.body.applyImpulse(new CANNON.Vec3(0, this.jumpForce * 0.9, 0), new CANNON.Vec3());
        this.hasDoubleJumped = true;
      }
    }
    
    // Cap maximum velocity to prevent excessive speeds
    const currentVelocity = new THREE.Vector3(
      this.body.velocity.x,
      0, // Ignore vertical component for speed cap
      this.body.velocity.z
    );
    
    const horizSpeed = currentVelocity.length();
    
    if (horizSpeed > this.maxSpeed) {
      const limitRatio = this.maxSpeed / horizSpeed;
      this.body.velocity.x *= limitRatio;
      this.body.velocity.z *= limitRatio;
    }
  }
  
  applyForces() {
    // This method is deprecated, using applyPhysicsForces instead
  }
  
  // Smoothly interpolate input values to reduce jittery movement
  smoothInput(currentValue, lastValueKey, smoothFactor) {
    // Initialize last value if not exists
    if (this[lastValueKey] === undefined) {
      this[lastValueKey] = currentValue;
    }
    
    // Calculate the smoothed value
    const smoothedValue = this[lastValueKey] + (currentValue - this[lastValueKey]) * smoothFactor;
    
    // Store the new smoothed value for next frame
    this[lastValueKey] = smoothedValue;
    
    return smoothedValue;
  }
  
  freeze() {
    this.isFrozen = true;
    // Store current velocity and angular velocity
    this.savedVelocity = this.body.velocity.clone();
    this.savedAngularVelocity = this.body.angularVelocity.clone();
    
    // Set velocities to zero
    this.body.velocity.set(0, 0, 0);
    this.body.angularVelocity.set(0, 0, 0);
    
    // Make the body static
    this.body.type = CANNON.Body.STATIC;
    
    if (this.debug) {
      console.log(`Car ${this.team} is now frozen`);
    }
  }
  
  unfreeze() {
    this.isFrozen = false;
    // Make the body dynamic again
    this.body.type = CANNON.Body.DYNAMIC;
    
    // Optionally restore previous velocity
    if (this.savedVelocity) {
      this.body.velocity.copy(this.savedVelocity);
      this.body.angularVelocity.copy(this.savedAngularVelocity);
      
      // Clear saved velocities to prevent reuse
      this.savedVelocity = null;
      this.savedAngularVelocity = null;
    } else {
      // If no saved velocity, ensure we start with zero velocity
      this.body.velocity.set(0, 0, 0);
      this.body.angularVelocity.set(0, 0, 0);
    }
    
    // Double-check if controls were reset during freeze
    if (this.controls.forward === 0 && 
        this.controls.backward === 0 && 
        this.controls.left === 0 && 
        this.controls.right === 0) {
      // Controls are all zero, this could happen during a freeze/unfreeze transition
      console.log(`Car ${this.team} unfrozen with zero controls - ensuring car responds`);
    }
    
    if (this.debug) {
      console.log(`Car ${this.team} is now unfrozen`);
      console.log(`Car body type after unfreeze: ${this.body.type}`);
    }
  }
  
  setControls(controls) {
    // Store the controls state
    this.controls = controls;
    
    // Track previous jump state to detect button press and release
    const jumpJustPressed = controls.jump && !this.prevJumpState;
    this.prevJumpState = controls.jump;
    
    // CRITICAL: DIRECT VELOCITY CONTROL
    // This bypasses the physics force application which may be failing
    
    // Ensure we're in a valid state to move
    if (this.body.type !== CANNON.Body.DYNAMIC) {
      this.body.type = CANNON.Body.DYNAMIC;
    }
    this.body.wakeUp();
    
    // Get car direction vectors
    const carQuat = this.mesh.quaternion;
    const forward = new THREE.Vector3(0, 0, 1).applyQuaternion(carQuat);
    const right = new THREE.Vector3(1, 0, 0).applyQuaternion(carQuat);
    
    // DIRECT VELOCITY CONTROL for immediate feedback
    // Base parameters - INCREASED for better responsiveness
    const baseSpeed = 45; // Reduced from 80 for more manageable forward movement
    const turnSpeed = 3.5; // Reduced for smoother turning
    const jumpVelocity = 18;
    const boostMultiplier = 1.8;
    
    // Handle forward/backward movement
    if (controls.forward > 0) {
      this.body.velocity.x = forward.x * baseSpeed * controls.forward;
      this.body.velocity.z = forward.z * baseSpeed * controls.forward;
    } else if (controls.backward > 0) {
      this.body.velocity.x = forward.x * -baseSpeed * 0.7 * controls.backward;
      this.body.velocity.z = forward.z * -baseSpeed * 0.7 * controls.backward;
    } else {
      // Apply friction when no forward/backward input
      this.body.velocity.x *= 0.95;
      this.body.velocity.z *= 0.95;
    }
    
    // Simple turning - only affects Y rotation
    if (controls.left > 0) {
      // Set only Y component for pure rotation around vertical axis
      this.body.angularVelocity.set(0, turnSpeed, 0);
    } else if (controls.right > 0) {
      // Set only Y component for pure rotation around vertical axis
      this.body.angularVelocity.set(0, -turnSpeed, 0);
    } else {
      // Stop turning when no input
      this.body.angularVelocity.set(0, 0, 0);
    }
    
    // Apply boost if active
    if (controls.boost && this.boostAmount > 0) {
      if (controls.forward > 0) {
        this.body.velocity.x *= boostMultiplier;
        this.body.velocity.z *= boostMultiplier;
      }
      this.boostAmount = Math.max(0, this.boostAmount - 0.5);
    }
    
    // Handle jumping with direct velocity
    if (jumpJustPressed && this.isOnGround && this.jumpCooldown === 0) {
      // Add upward velocity directly
      this.body.velocity.y = jumpVelocity;
      this.jumpCooldown = 10;
      this.canDoubleJump = true;
    } else if (jumpJustPressed && !this.isOnGround && this.canDoubleJump) {
      // Double jump
      this.body.velocity.y = jumpVelocity * 0.9;
      this.canDoubleJump = false;
      this.hasDoubleJumped = true;
    }
    
    // Update boost effect
    if (controls.boost && this.boostAmount > 0) {
      if (!this.boostEffect) {
        this.createBoostEffect();
      }
    } else if (this.boostEffect) {
      // Hide boost effect when not boosting
      this.boostEffect.visible = false;
    }
  }
  
  getPosition() {
    return this.body.position;
  }
  
  getQuaternion() {
    return this.body.quaternion;
  }
  
  reset() {
    // Reset position to original start position
    this.body.position.set(
      this.startPosition.x,
      this.startPosition.y,
      this.startPosition.z
    );
    
    // Reset rotation to default (facing forward)
    this.body.quaternion.set(0, 0, 0, 1);
    
    // Reset velocities
    this.body.velocity.set(0, 0, 0);
    this.body.angularVelocity.set(0, 0, 0);
    
    // CRITICAL: Ensure the car is in dynamic mode and is not frozen
    this.body.type = CANNON.Body.DYNAMIC;
    this.isFrozen = false;
    
    // Force body to wake up
    this.body.wakeUp();
    
    // Make sure mesh follows physics body
    this.mesh.position.copy(this.body.position);
    this.mesh.quaternion.copy(this.body.quaternion);
    
    // Reset double jump state
    this.hasDoubleJumped = false;
    this.canDoubleJump = false;
    
    // Reset boost
    this.boostAmount = 100;
    this.updateBoostBar();
    
    if (this.debug) {
      console.log(`Car ${this.team} reset to:`, {
        position: [this.body.position.x, this.body.position.y, this.body.position.z],
        isFrozen: this.isFrozen,
        type: this.body.type
      });
    }
  }
  
  updateBoostBar() {
    // Update boost meter UI
    const boostFill = document.querySelector('.boost-meter-fill');
    const boostText = document.querySelector('.boost-text');
    
    if (boostFill) {
      // Update the visual fill bar
      boostFill.style.transform = `scaleX(${this.boostAmount / 100})`;
      
      // Update color based on amount
      if (this.boostAmount < 20) {
        boostFill.style.backgroundColor = '#ff3333'; // Red when low
      } else if (this.boostAmount < 50) {
        boostFill.style.backgroundColor = '#ffaa00'; // Orange when medium
      } else {
        boostFill.style.backgroundColor = '#00ff44'; // Green when high
      }
      
      // Add pulsing effect when boosting
      if (this.controls.boost && this.boostAmount > 0) {
        boostFill.style.filter = 'brightness(1.5)';
        boostFill.style.boxShadow = '0 0 8px rgba(255, 255, 255, 0.7)';
      } else {
        boostFill.style.filter = 'brightness(1)';
        boostFill.style.boxShadow = 'none';
      }
      
      // If boost is 0, make sure UI shows empty
      if (this.boostAmount === 0) {
        boostFill.style.transform = 'scaleX(0)';
      }
    }
    
    // Update the numeric display
    if (boostText) {
      const boostValue = Math.round(this.boostAmount);
      boostText.textContent = `BOOST: ${boostValue}`;
      
      // Make text pulse when boosting
      if (this.controls.boost && this.boostAmount > 0) {
        boostText.style.color = '#ffffff';
        boostText.style.textShadow = '0 0 5px #ffffff';
      } else {
        boostText.style.color = '#ffffff';
        boostText.style.textShadow = '1px 1px 2px black';
      }
    }
  }
  
  // Check collision with ball and apply power hit if conditions are met
  checkBallCollision() {
    // Get reference to game ball from physics world
    if (!this.physicsWorld.game || !this.physicsWorld.game.ball) return;
    
    const ball = this.physicsWorld.game.ball;
    
    // Simple spherical collision detection
    const carPos = this.body.position;
    const ballPos = ball.body.position;
    
    // Calculate distance between car and ball centers
    const dx = carPos.x - ballPos.x;
    const dy = carPos.y - ballPos.y;
    const dz = carPos.z - ballPos.z;
    const distance = Math.sqrt(dx*dx + dy*dy + dz*dz);
    
    // Check if collision occurred (car radius + ball radius)
    const collisionThreshold = 3 + ball.radius;
    
    if (distance <= collisionThreshold) {
      // Check if this is a high-speed hit
      const carSpeed = this.body.velocity.length();
      
      // Calculate contact point (approximation)
      const contactDirection = new THREE.Vector3(dx, dy, dz).normalize();
      const contactPoint = {
        x: ballPos.x + contactDirection.x * ball.radius,
        y: ballPos.y + contactDirection.y * ball.radius,
        z: ballPos.z + contactDirection.z * ball.radius
      };
      
      // For powerful hits, trigger the new power hit mechanic
      if (carSpeed > 10 || this.controls.boost) {
        ball.hitByPlayer(this.body.velocity, contactPoint);
        
        // Add visual/audio feedback here when we have those assets
        if (this.debug) {
          console.log(`Car ${this.team} hit the ball at speed: ${carSpeed}`);
        }
      }
    }
  }
  
  createBoostEffect() {
    // Create a simple particle system for boost effect if it doesn't exist
    if (!this.boostParticles) {
      // Create particle container
      this.boostParticles = {
        particles: [],
        maxParticles: 30,
        lastEmitTime: 0
      };
    }
    
    // Only emit particles every few milliseconds to control density
    const now = Date.now();
    if (now - this.boostParticles.lastEmitTime > 50) {
      this.boostParticles.lastEmitTime = now;
      
      // Get car's backward direction
      const backwardDir = new THREE.Vector3(0, 0, -1);
      backwardDir.applyQuaternion(this.mesh.quaternion);
      
      // Create boost particle
      const particle = {
        position: new THREE.Vector3(
          this.mesh.position.x + backwardDir.x * 1.7,
          this.mesh.position.y - 0.2,
          this.mesh.position.z + backwardDir.z * 1.7
        ),
        velocity: new THREE.Vector3(
          backwardDir.x * 2 + (Math.random() - 0.5) * 0.5,
          0.1 + Math.random() * 0.2,
          backwardDir.z * 2 + (Math.random() - 0.5) * 0.5
        ),
        size: 0.2 + Math.random() * 0.3,
        color: this.team === 'blue' ? 0x00aaff : 0xff7700,
        lifetime: 400, // milliseconds
        createTime: now,
        mesh: null
      };
      
      // Create particle mesh
      const geometry = new THREE.SphereGeometry(particle.size, 8, 8);
      const material = new THREE.MeshBasicMaterial({
        color: particle.color,
        transparent: true,
        opacity: 0.8
      });
      
      particle.mesh = new THREE.Mesh(geometry, material);
      particle.mesh.position.copy(particle.position);
      
      // Add to scene
      this.scene.scene.add(particle.mesh);
      
      // Add to particles array
      this.boostParticles.particles.push(particle);
      
      // Limit number of particles
      if (this.boostParticles.particles.length > this.boostParticles.maxParticles) {
        // Remove oldest particle
        const oldestParticle = this.boostParticles.particles.shift();
        this.scene.scene.remove(oldestParticle.mesh);
        oldestParticle.mesh.geometry.dispose();
        oldestParticle.mesh.material.dispose();
      }
    }
  }
  
  updateBoostParticles(delta) {
    // Update boost particles if they exist
    if (this.boostParticles && this.boostParticles.particles.length > 0) {
      const now = Date.now();
      
      // Loop through all particles
      for (let i = this.boostParticles.particles.length - 1; i >= 0; i--) {
        const particle = this.boostParticles.particles[i];
        const age = now - particle.createTime;
        
        // Remove expired particles
        if (age > particle.lifetime) {
          this.scene.scene.remove(particle.mesh);
          particle.mesh.geometry.dispose();
          particle.mesh.material.dispose();
          this.boostParticles.particles.splice(i, 1);
          continue;
        }
        
        // Update particle position
        particle.position.add(particle.velocity);
        particle.mesh.position.copy(particle.position);
        
        // Fade out based on age
        const opacity = 1 - (age / particle.lifetime);
        particle.mesh.material.opacity = opacity;
        
        // Grow particles slightly as they age
        const scale = 1 + (age / particle.lifetime);
        particle.mesh.scale.set(scale, scale, scale);
      }
    }
  }
} 